import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AudioSchedulerContext } from "../audio/audioSchedulerContext";
import { useMuted } from "../audio/mutedContext";
import { useConfig } from "../config/configContext";
import { Phase, Room } from "../config/parsedConfig";
import { CurrentSessionInfo, useCurrentSession } from "../hooks/useCurrentSession";
import { useUpdateTitle } from "../hooks/useUpdateTitle";
import { randomElement, randomInt } from "../utils/random";
import { convertSecondsToTimestamp, getNowMaybeSimulated } from "../utils/time";
import NotFound from "./404";
import { NowContext } from "../hooks/nowContext";
import ClockLayoutRoomDisplay from "../components/clockLayout/ClockLayoutRoomDisplay";
import LinearLayoutRoomDisplay from "../components/linearLayout/LinearLayoutRoomDisplay";

export interface PlannedPhase extends Phase {
    /**
     * The number of seconds between the end of this phase and the end of the entire session.
     * The last phase of the session has ``displayPhaseEndToSessionEnd = 0``.
     * This number is used to determine the angle-position of the RingElement that displays the phase.
     */
    displayPhaseEndToSessionEnd?: number;
    /**
     * The number of seconds between the start of this phase and the end of the entire session.
     * This number is used to determine the angle-position of the RingElement that displays the phase.
     * For phases with ``hiddenOnClock = true``, this number equals ``displayPhaseEndToSessionEnd``.
     */
    displayPhaseStartToSessionEnd?: number;
    /** seconds that this phase has been running already */
    elapsedTime?: number;
    /** seconds that are left in this phase */
    remainingTime?: number;
}

/**
 * Calculates the upcoming phases of a session. 
 * @param param0 
 * @returns An array of phases that are upcoming in the session. The first phase is the current phase. 
 * It's duration is shortened by the time that has already passed in the current phase.
 */
export const getUpcomingAndPastPhases = (phases: Phase[], currentSessionRunningTime: number, keepLastPhase?: boolean) => {
    const upcomingPhases: PlannedPhase[] = [];
    const pastPhases: PlannedPhase[] = [];
    //iterate over phases
    //use ``currentSessionRunningTime`` to skip over all phases that are already over and to cut off the already-passed part of the current phase
    //add those (modified) phases to ``statusPhases``
    phases.forEach(phase => {
        if (currentSessionRunningTime <= 0) {
            //phase is in the future
            upcomingPhases.push({
                ...phase,
                elapsedTime: 0,
                remainingTime: phase.duration,
            });
        } else if (phase.duration > currentSessionRunningTime) {
            //current phase
            upcomingPhases.push({
                ...phase,
                elapsedTime: currentSessionRunningTime,
                remainingTime: phase.duration - currentSessionRunningTime,
            });
            currentSessionRunningTime = 0;
        } else {
            //phase is in the past. don't add it to upcomingPhases. 
            //subtract its duration from currentSessionRunningTime for this forEach-loop
            currentSessionRunningTime -= phase.duration;
            pastPhases.push({
                ...phase,
                elapsedTime: phase.duration,
                remainingTime: 0,
            });
        }
    });
    if (keepLastPhase && upcomingPhases.length === 0 && phases.length > 0) {
        //add last phase with duration 0
        upcomingPhases.push({
            ...phases.at(-1)!,
            elapsedTime: phases.at(-1)!.duration,
            remainingTime: 0
        });
    }
    //calculate the phaseEndToSessionEnd for each phase
    //iterate upcomingPhases backwards and add the duration of each phase to the phaseEndToSessionEnd of the next phase
    let toSessionEnd = 0;
    for (let i = upcomingPhases.length - 1; i >= 0; i--) {
        upcomingPhases[i].displayPhaseEndToSessionEnd = toSessionEnd;
        if (!upcomingPhases[i].hiddenOnClock) {
            toSessionEnd += upcomingPhases[i].remainingTime ?? upcomingPhases[i].duration;
        }
        upcomingPhases[i].displayPhaseStartToSessionEnd = toSessionEnd;
    }
    // console.debug("upcomingPhases", {phases, currentSessionRunningTime, upcomingPhases});
    return [upcomingPhases, pastPhases];
}

export const DEFAULT_PAUSE_IMAGES = [
    "/img/Schreibtisch.png",
];

export const DEFAULT_DOOR_IMAGE = "/img/door-green.png";

export type RoomParams = {
    room: Room;
    inside: boolean;
    isPlayingAudio: boolean;
    currentSessionInfo: CurrentSessionInfo;
    currentPhase?: PlannedPhase;
    upcomingPhases: PlannedPhase[];
    pastPhases: PlannedPhase[];
    pauseImage: string;
    enterRoom: () => void;
    leaveRoom: () => void;
    joinZoom: () => void;
}

export const RoomDisplay = ({ room }: { room: Room }) => {
    const currentSessionInfo = useCurrentSession(room);
    const {
        currentSession,
        upcomingSession,
        // upcomingSessionIsTomorrow,
        currentSessionRunningTime,
        timeUntilUpcomingSession,
        currentDateInTimezone,
    } = currentSessionInfo;

    const [audioScheduler, playingAudio] = useContext(AudioSchedulerContext);
    const { timeshift } = useContext(NowContext);

    const config = useConfig();

    const [inside, setInside] = useState(false);
    const isPlayingAudio = playingAudio !== null;

    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [muted, setMuted] = useMuted();

    const joinZoom = useCallback(() => {
        if (room.zoomLink) {
            //join zoom: open link from room in new tab
            window.open(room.zoomLink, "_blank");
        }
    }, [room.zoomLink]);

    const leaveRoom = useCallback(() => {
        //stop audio and set inside to false
        audioScheduler.stopAllIncludingSilence();
        setInside(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const enterRoom = useCallback(() => {
        if (!inside) {
            setInside(true);
            setMuted(false);
            audioScheduler.playSilence();

            if (room.zoomLink) {
                //auto join zoom?
                const autoJoinZoom = currentSession
                    ? (!!config.zoom.autoJoin_afterSessionStart && currentSessionRunningTime <= config.zoom.autoJoin_afterSessionStart)
                    : (!!config.zoom.autoJoin_beforeSessionStart && timeUntilUpcomingSession <= config.zoom.autoJoin_beforeSessionStart);
                if (autoJoinZoom) {
                    joinZoom();
                }
            }
        }
    }, [inside, setMuted, audioScheduler, room.zoomLink, currentSession, config.zoom.autoJoin_afterSessionStart, config.zoom.autoJoin_beforeSessionStart, currentSessionRunningTime, timeUntilUpcomingSession, joinZoom]);

    const theDiceAreThrown = useMemo(() => {
        let currentSeed = currentDateInTimezone + "_" + room.name + (currentSession ? "_" + currentSession.name + "_" + currentSession.startTime : "");
        let upcomingSeed = currentDateInTimezone + "_" + room.name + "_" + upcomingSession.name + "_" + upcomingSession.startTime;
        const currentAudioFlaverIndex = randomInt(currentSession?.audioFlavors.length || 0, currentSeed);
        const upcomingAudioFlaverIndex = randomInt(upcomingSession.audioFlavors.length || 0, upcomingSeed);
        const pauseImage = randomElement(room.pauseImages || DEFAULT_PAUSE_IMAGES, upcomingSeed);
        console.debug("theDiceAreThrown 🎲", { currentSeed, upcomingSeed, currentAudioFlaverIndex, upcomingAudioFlaverIndex, pauseImage })
        return {
            currentAudioFlaverIndex,
            upcomingAudioFlaverIndex,
            pauseImage
        };
    }, [currentDateInTimezone, room, currentSession, upcomingSession]);

    const allAudios = useMemo(() => {
        return [
            ...(currentSession ? currentSession.phases.map(phase => phase.audio?.[theDiceAreThrown.currentAudioFlaverIndex]) : []),
            ...(upcomingSession !== currentSession ? upcomingSession.phases.map(phase => phase.audio?.[theDiceAreThrown.upcomingAudioFlaverIndex]) : [])
        ].filter(audio => audio) as string[];
    }, [currentSession, upcomingSession, theDiceAreThrown]);

    //preload all audios
    useEffect(() => {
        audioScheduler.preload(allAudios);
    }, [audioScheduler, allAudios]);

    const [upcomingPhases, pastPhases] = useMemo(() => {
        if (!currentSession) {
            return [[], []];
        }
        return getUpcomingAndPastPhases(currentSession.phases, currentSessionRunningTime);
    }, [currentSession, currentSessionRunningTime]);

    const currentPhase = upcomingPhases[0];

    /**
     * The audio that is currently playing or will be played next, together with the startTime of its phase.
     * Null if no audio is scheduled.
     */
    const currentOrNextAudio = useMemo(() => {
        if (!inside) return null;

        //check if there is an audio that is currently playing
        //iterate over upcoming phases and return the first one that has audio
        const upcomingPhaseWithAudio = upcomingPhases.find(phase => phase.audio);
        if (upcomingPhaseWithAudio) {

            return {
                url: upcomingPhaseWithAudio.audio![theDiceAreThrown.currentAudioFlaverIndex],
                timestamp: convertSecondsToTimestamp(upcomingPhaseWithAudio.startTime, config.timezone, upcomingPhaseWithAudio !== upcomingPhases?.[0])
            };
        }
        //no audio found in the current session. check upcoming session
        const upcomingSessionPhaseWithAudio = upcomingSession.phases.find(phase => phase.audio);
        if (upcomingSessionPhaseWithAudio) {
            return {
                url: upcomingSessionPhaseWithAudio.audio![theDiceAreThrown.upcomingAudioFlaverIndex],
                timestamp: convertSecondsToTimestamp(upcomingSessionPhaseWithAudio.startTime, config.timezone, true)
            };
        }
        //no audio found in the upcoming session. return null
        return null;
    }, [inside, upcomingPhases, upcomingSession, theDiceAreThrown, config.timezone]);

    //play (or schedule) the audio
    useEffect(() => {
        // console.debug("currentAudio changed", { url: currentOrNextAudio?.url, timestamp: currentOrNextAudio?.timestamp });
        if (currentOrNextAudio?.url) {
            if (timeshift) {
                console.debug("⏱️ timeshift active (", timeshift/1000, "s) -> consider this for scheduling audio ", {currentOrNextAudio, now: getNowMaybeSimulated()});
            }
            //play audio
            audioScheduler.playAt(currentOrNextAudio.url, currentOrNextAudio?.timestamp);
        }
        // return () => {
        //     // console.debug("currentAudio cleanup stop", { url: currentOrNextAudio?.url, timestamp: currentOrNextAudio?.timestamp });
        //     audioScheduler.stop();
        // }
    }, [audioScheduler, currentOrNextAudio, timeshift]);


    useUpdateTitle({ currentPhase, inside, timeUntilUpcomingSession, roomName: room.displayName });

    const RoomDisplayComponent = room.linearLayout ? LinearLayoutRoomDisplay : ClockLayoutRoomDisplay;

    return <RoomDisplayComponent
        room={room}
        inside={inside}
        isPlayingAudio={isPlayingAudio}
        currentSessionInfo={currentSessionInfo}
        currentPhase={currentPhase}
        upcomingPhases={upcomingPhases}
        pastPhases={pastPhases}
        enterRoom={enterRoom}
        leaveRoom={leaveRoom}
        joinZoom={joinZoom}
        pauseImage={theDiceAreThrown.pauseImage}
    />
};

export const RoomRoute = () => {
    const { roomName } = useParams();
    const config = useConfig();
    const room = (roomName && config.rooms[roomName]) || undefined;
    useEffect(() => {
        if (room?.scale) {
            //add to body.style transform: scale()
            document.body.style.transform = `scale(${room.scale})`;
            document.body.style.transformOrigin = "center";
        }
        return () => {
            //remove from body.style transform: scale()
            document.body.style.transform = "";
        }
    }, [room?.scale]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!room) {
        return (
            <NotFound />
        );
    }
    return <RoomDisplay room={room} />;
};
